import { db } from '../../../../firebase';
import {
  collection,
  collectionGroup,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import settlementService from './footballSettlement';

class PredictionAccuracyService {
  constructor() {
    this.predictionEvents = 'predictionUsage';  // Correct collection name!
    this.confidenceLevels = {
      HIGH: { min: 70, max: 100, label: 'HIGH' },
      MED: { min: 40, max: 69, label: 'MEDIUM' },
      LOW: { min: 0, max: 39, label: 'LOW' }
    };
  }

  // Convert NYC ET date to Firebase timestamps for the full day
  getTimestampRangeForETDate(date) {
    const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];

    // Create start of day in ET
    const startOfDayET = new Date(`${dateStr}T00:00:00`);
    const startTimestamp = Timestamp.fromDate(startOfDayET);

    // Create end of day in ET
    const endOfDayET = new Date(`${dateStr}T23:59:59`);
    const endTimestamp = Timestamp.fromDate(endOfDayET);

    return { startTimestamp, endTimestamp };
  }

  // Fetch all football predictions for a specific date
  async getPredictionsForDate(date) {
    try {
      const { startTimestamp, endTimestamp } = this.getTimestampRangeForETDate(date);

      // Use collectionGroup to query across all user's events subcollections
      const q = query(
        collectionGroup(db, 'events'),
        where('sport', '==', 'FOOTBALL'),
        where('timestamp', '>=', startTimestamp),
        where('timestamp', '<=', endTimestamp),
        orderBy('timestamp', 'desc')
      );

      const snapshot = await getDocs(q);
      const predictions = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.predictions && Array.isArray(data.predictions)) {
          data.predictions.forEach((pred, index) => {
            // Find the corresponding game metadata
            const gameMetadata = data.metadata?.games?.[index] || data.metadata?.games?.[0] || {};

            predictions.push({
              docId: doc.id,
              userId: data.userId,
              timestamp: data.timestamp,
              gameId: pred.gameId || pred.sportGameId?.split('_')[1],
              sportGameId: pred.sportGameId,
              winner: pred.winner,
              confidence: pred.confidence,
              confidenceNumeric: pred.confidenceNumeric || 50, // Default to 50 if missing
              reason: pred.reason,
              full: pred.full,
              home: gameMetadata.home || 'Unknown',
              away: gameMetadata.away || 'Unknown',
              tier: data.metadata?.tier || data.tier || 'basic',
              starCoinsUsed: Math.ceil((data.starCoinsUsed || 7) / (data.gamesCount || 1)), // Cost per game
              failedPredictions: data.failedPredictions || 0,
              cachedUsed: data.metadata?.cachedUsed || 0
            });
          });
        }
      });

      console.log(`📊 Found ${predictions.length} football predictions for ${date}`);
      return predictions;
    } catch (error) {
      console.error('Error fetching predictions:', error);
      return [];
    }
  }

  // Match predictions with actual game results
  async matchPredictionsWithResults(date) {
    const dateStr = typeof date === 'string' ? date :
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    console.log(`🔄 Matching predictions with results for ${dateStr}`);

    // Get predictions and settlements
    const [predictions, settlements] = await Promise.all([
      this.getPredictionsForDate(date),
      settlementService.getSettlementsByDate(date)
    ]);

    if (!settlements) {
      console.log('⚠️ No settlement data available for this date');
      return [];
    }

    const matched = [];

    for (const prediction of predictions) {
      const gameId = String(prediction.gameId);
      const settlement = settlements[gameId];

      if (settlement) {
        const actualResult = this.determineActualWinner(settlement);
        const predictionCorrect = this.isPredictionCorrect(prediction.winner, actualResult, settlement);

        matched.push({
          ...prediction,
          settlement: {
            gameId: settlement.gameId,
            home: settlement.teams.home.name,
            away: settlement.teams.away.name,
            homeScore: settlement.score?.home,
            awayScore: settlement.score?.away,
            result: settlement.result,
            status: settlement.status?.short,
            actualWinner: actualResult.winner,
            actualResult: actualResult.result
          },
          isCorrect: predictionCorrect,
          isSettled: settlement.isSettled || ['FT', 'AET', 'PEN'].includes(settlement.status?.short),
          isPending: ['NS', 'TBD', 'LIVE'].includes(settlement.status?.short)
        });
      } else {
        // No settlement data yet
        matched.push({
          ...prediction,
          settlement: null,
          isCorrect: null,
          isSettled: false,
          isPending: true
        });
      }
    }

    return matched;
  }

  // Determine the actual winner from settlement data
  determineActualWinner(settlement) {
    if (!settlement.score || settlement.score.home === null || settlement.score.away === null) {
      return { winner: null, result: 'PENDING' };
    }

    const homeScore = settlement.score.home;
    const awayScore = settlement.score.away;

    if (homeScore > awayScore) {
      return {
        winner: settlement.teams.home.name,
        result: 'HOME_WIN'
      };
    } else if (awayScore > homeScore) {
      return {
        winner: settlement.teams.away.name,
        result: 'AWAY_WIN'
      };
    } else {
      return {
        winner: 'Draw',
        result: 'DRAW'
      };
    }
  }

  // Check if prediction was correct
  isPredictionCorrect(predictedWinner, actualResult, settlement) {
    if (!actualResult.winner || !predictedWinner) {
      return null; // Can't determine yet
    }

    // Normalize names for comparison (case-insensitive)
    const predicted = predictedWinner.toLowerCase().trim();
    const homeName = settlement.teams.home.name.toLowerCase().trim();
    const awayName = settlement.teams.away.name.toLowerCase().trim();
    const actual = actualResult.winner.toLowerCase().trim();

    // Check if prediction matches
    if (predicted === actual) {
      return true;
    }

    // Check partial name matches (e.g., "Man United" vs "Manchester United")
    if (predicted.includes(homeName) || homeName.includes(predicted)) {
      return actualResult.result === 'HOME_WIN';
    }

    if (predicted.includes(awayName) || awayName.includes(predicted)) {
      return actualResult.result === 'AWAY_WIN';
    }

    // Check for draw predictions
    if (predicted === 'draw' || predicted === 'tie') {
      return actualResult.result === 'DRAW';
    }

    return false;
  }

  // Calculate accuracy statistics
  async calculateAccuracyStats(date) {
    const matched = await this.matchPredictionsWithResults(date);

    const stats = {
      date: date,
      total: matched.length,
      settled: 0,
      correct: 0,
      incorrect: 0,
      pending: 0,
      accuracy: 0,
      byConfidence: {
        HIGH: { total: 0, correct: 0, accuracy: 0 },
        MED: { total: 0, correct: 0, accuracy: 0 },
        LOW: { total: 0, correct: 0, accuracy: 0 }
      },
      byTier: {},
      starCoinsUsed: 0,
      starCoinsWasted: 0
    };

    for (const match of matched) {
      stats.starCoinsUsed += match.starCoinsUsed || 0;

      if (match.isSettled) {
        stats.settled++;

        if (match.isCorrect) {
          stats.correct++;
        } else {
          stats.incorrect++;
          stats.starCoinsWasted += match.starCoinsUsed || 0;
        }

        // Track by confidence level
        const confidence = match.confidence || 'MED';
        if (stats.byConfidence[confidence]) {
          stats.byConfidence[confidence].total++;
          if (match.isCorrect) {
            stats.byConfidence[confidence].correct++;
          }
        }

        // Track by tier
        const tier = match.tier || 'unknown';
        if (!stats.byTier[tier]) {
          stats.byTier[tier] = { total: 0, correct: 0, accuracy: 0 };
        }
        stats.byTier[tier].total++;
        if (match.isCorrect) {
          stats.byTier[tier].correct++;
        }
      } else {
        stats.pending++;
      }
    }

    // Calculate accuracy percentages
    if (stats.settled > 0) {
      stats.accuracy = ((stats.correct / stats.settled) * 100).toFixed(2);
    }

    // Calculate confidence level accuracies
    for (const level in stats.byConfidence) {
      const conf = stats.byConfidence[level];
      if (conf.total > 0) {
        conf.accuracy = ((conf.correct / conf.total) * 100).toFixed(2);
      }
    }

    // Calculate tier accuracies
    for (const tier in stats.byTier) {
      const t = stats.byTier[tier];
      if (t.total > 0) {
        t.accuracy = ((t.correct / t.total) * 100).toFixed(2);
      }
    }

    return { stats, details: matched };
  }

  // Get predictions for a date range
  async getAccuracyTrend(startDate, endDate) {
    const trend = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
      const dateStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
      const dayStats = await this.calculateAccuracyStats(dateStr);

      trend.push({
        date: dateStr,
        ...dayStats.stats
      });

      current.setDate(current.getDate() + 1);
    }

    return trend;
  }

  // Get user-specific accuracy
  async getUserAccuracy(userId, date) {
    const matched = await this.matchPredictionsWithResults(date);
    const userPredictions = matched.filter(m => m.userId === userId);

    const stats = {
      userId,
      date,
      total: userPredictions.length,
      correct: userPredictions.filter(p => p.isCorrect === true).length,
      incorrect: userPredictions.filter(p => p.isCorrect === false).length,
      pending: userPredictions.filter(p => p.isPending).length,
      accuracy: 0
    };

    const settled = stats.correct + stats.incorrect;
    if (settled > 0) {
      stats.accuracy = ((stats.correct / settled) * 100).toFixed(2);
    }

    return stats;
  }

  // Get top performing users for a date
  async getTopPerformers(date, minPredictions = 3) {
    const matched = await this.matchPredictionsWithResults(date);

    // Group by user
    const userStats = {};

    matched.forEach(match => {
      const userId = match.userId;
      if (!userStats[userId]) {
        userStats[userId] = {
          userId,
          total: 0,
          correct: 0,
          incorrect: 0,
          pending: 0,
          accuracy: 0,
          starCoinsUsed: 0
        };
      }

      userStats[userId].total++;
      userStats[userId].starCoinsUsed += match.starCoinsUsed || 0;

      if (match.isSettled) {
        if (match.isCorrect) {
          userStats[userId].correct++;
        } else {
          userStats[userId].incorrect++;
        }
      } else {
        userStats[userId].pending++;
      }
    });

    // Calculate accuracies and filter
    const performers = Object.values(userStats)
      .filter(user => (user.correct + user.incorrect) >= minPredictions)
      .map(user => {
        const settled = user.correct + user.incorrect;
        user.accuracy = settled > 0 ? ((user.correct / settled) * 100).toFixed(2) : 0;
        return user;
      })
      .sort((a, b) => b.accuracy - a.accuracy);

    return performers;
  }

  // Export results to CSV format
  exportToCSV(matched) {
    const headers = [
      'Date',
      'User ID',
      'Game ID',
      'Home Team',
      'Away Team',
      'Predicted Winner',
      'Confidence',
      'Confidence %',
      'Actual Winner',
      'Home Score',
      'Away Score',
      'Is Correct',
      'Star Coins Used',
      'Tier'
    ];

    const rows = matched.map(m => [
      m.timestamp?.toDate?.()?.toISOString() || '',
      m.userId || '',
      m.gameId || '',
      m.settlement?.home || m.home || '',
      m.settlement?.away || m.away || '',
      m.winner || '',
      m.confidence || '',
      m.confidenceNumeric || '',
      m.settlement?.actualWinner || 'Pending',
      m.settlement?.homeScore ?? '',
      m.settlement?.awayScore ?? '',
      m.isCorrect === null ? 'Pending' : m.isCorrect ? 'Yes' : 'No',
      m.starCoinsUsed || 0,
      m.tier || ''
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    return csv;
  }
}

const predictionAccuracyService = new PredictionAccuracyService();

// Make available globally for debugging - ONLY IN DEVELOPMENT
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.predictionAccuracy = predictionAccuracyService;
  console.log('💯 Prediction Accuracy Service available as window.predictionAccuracy');
  console.log('Try: await window.predictionAccuracy.calculateAccuracyStats("2025-09-17")');
}

export default predictionAccuracyService;